import { lazy, Suspense } from "react";
import cimfoniLogo from '@/assets/cimfoni_logo_transparent.png';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { BrandingProvider } from "./contexts/BrandingContext";
import { OfflineProvider } from "./contexts/OfflineContext";
import { OfflineIndicator } from "./components/OfflineIndicator";
import { useRealtimeSync } from "./hooks/useRealtimeSync";
import Index from "./pages/Index";
// Cadence floating chat temporarily disabled — see USERGUIDE_REMOVED_SECTIONS.md
// import { FloatingHelpChat } from "./components/FloatingHelpChat";

const NotFound = lazy(() => import("./pages/NotFound"));
const KioskView = lazy(() => import("./components/KioskView").then(m => ({ default: m.KioskView })));
const DownloadStatus = lazy(() => import("./pages/DownloadStatus"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword").then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import("./pages/ResetPassword").then(m => ({ default: m.ResetPassword })));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const ComplianceVawaHipaa = lazy(() => import("./pages/ComplianceVawaHipaa"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));

const queryClient = new QueryClient();

const RealtimeSyncProvider = ({ children }: { children: React.ReactNode }) => {
  useRealtimeSync();
  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <BrandingProvider>
        <OfflineProvider>
          <RealtimeSyncProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <main>
                <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><img src={cimfoniLogo} alt="Loading" className="h-16 w-16 animate-spin" /></div>}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/login" element={<Navigate to="/" replace />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/kiosk" element={<KioskView />} />
                    <Route path="/download-status" element={<DownloadStatus />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/guides/compliance-vawa-hipaa" element={<ComplianceVawaHipaa />} />
                    <Route path="/terms-of-service" element={<TermsOfService />} />
                    <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </main>
              
              <OfflineIndicator />
              {/* <FloatingHelpChat /> */}
            </BrowserRouter>
          </TooltipProvider>
          </RealtimeSyncProvider>
        </OfflineProvider>
      </BrandingProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
